import { useState, FC, useEffect } from "react";
import { FilterPanel } from "./FilterPanel";
import {
	EventType,
	DanceStyle,
	Teacher,
	ViewOptions,
	TeacherNode,
	DanceStyleNode,
	EventTypeNode,
	ColorsData,
	DanceStyleData,
	CourseNode,
	EventNode,
	CalendarItem,
	CalendarNode,
} from "./types";
import { MonthlyView } from "./MonthlyView";
import "./App.css";
import { ViewType } from "./ViewType";
import { useApiData } from "./helpers";

const App: FC = () => {
	const [selectedDanceStyle, setSelectedDanceStyle] =
		useState<DanceStyle | null>(null);
	const [selectedEventType, setSelectedEventType] = useState<EventType | null>(
		null
	);

	const [legend, setLegend] = useState<ColorsData[]>([]);
	const [eventNodes, setEventNodes] = useState<EventNode[]>([]);
	const [courseNodes, setCourseNodes] = useState<CourseNode[]>([]);
	const [calendarItems, setCalendarItems] = useState<CalendarItem[]>([]);
	const [eventTypeNodes, setEventTypeNodes] = useState<EventTypeNode[]>([]);
	const [danceStyleNodes, setDanceStyleNodes] = useState<DanceStyleNode[]>([]);
	const [view, setView] = useState<ViewOptions>(ViewOptions.MONTHLY);

	const API_DOMAIN = "https://salsacubanaberlin.de/wp-json/wp/v2";

	useApiData(`${API_DOMAIN}/posts`, setCourseNodes);
	useApiData(`${API_DOMAIN}/event_type`, setEventTypeNodes);
	useApiData(`${API_DOMAIN}/categories`, setDanceStyleNodes);
	useApiData(`${API_DOMAIN}/cpt_event`, setEventNodes);

	const extraEventTypeOptionForPost = { value: 99999, label: "Kurs" };

	const eventTypeOptions = eventTypeNodes
		.map((type) => ({
			value: type.id,
			label: type.name,
		}))
		.concat(extraEventTypeOptionForPost)
		.sort((a, b) => b.value - a.value);

	const danceStyleOptions = danceStyleNodes.map((style) => ({
		value: style.id,
		label: style.name,
	}));

	const allOptions = {
		danceStyleOptions,
		eventTypeOptions,
	};

	useEffect(() => {
		const calendarItems = [...eventNodes, ...courseNodes] as CalendarNode[];

		const extractColors = (eventTypeNodes: EventTypeNode[]) => {
			return eventTypeNodes.map(({ id, acf: { farbe }, name }) => ({
				id,
				color: farbe,
				label: name,
			}));
		};

		const extractStyles = (danceStyleNodes: DanceStyleNode[]) => {
			return danceStyleNodes.map(({ id, name }) => ({
				id,
				name,
			}));
		};

		const colorsData: ColorsData[] = extractColors(
			eventTypeNodes as EventTypeNode[]
		);

		const root = document.querySelector("#root");
		let coursesColor = "#000000";
		coursesColor = root?.getAttribute("data-courses-color") ?? coursesColor;

		colorsData.push({
			id: extraEventTypeOptionForPost.value,
			color: coursesColor,
			label: extraEventTypeOptionForPost.label,
		});

		colorsData.sort((a, b) => {
			const labelA = a.label.toUpperCase(); // Ignore case
			const labelB = b.label.toUpperCase();

			if (labelA < labelB) {
				return -1;
			}
			if (labelA > labelB) {
				return 1;
			}

			// Labels are equal, no change in order
			return 0;
		});

		setLegend(colorsData);

		const stylesData: DanceStyleData[] = extractStyles(
			danceStyleNodes as DanceStyleNode[]
		);

		if (calendarItems) {
			const calendarItemsWithColorsAndStyleTags = calendarItems.map((dataItem) => {
				const event_type =
					"event_type" in dataItem && dataItem.event_type
						? dataItem.event_type[0]
						: extraEventTypeOptionForPost.value;

				const eventTypeNode = eventTypeNodes.find((item) => item.id === event_type);

				const danceStyleIds = dataItem.categories;

				const matchingColor = extraEventTypeOptionForPost.value
					? colorsData.find((item) => item.id === event_type)
					: null;

				const matchingStyles = danceStyleIds
					? danceStyleIds.map((styleId) =>
							stylesData.find((style) => style.id === styleId)
					  )
					: [];

				return {
					...dataItem,
					//only add event_type if it does not exist
					event: "event_type" in dataItem && dataItem.event_type ? true : false,
					event_type: [event_type],
					event_label: eventTypeNode
						? eventTypeNode.name
						: extraEventTypeOptionForPost.label,
					color: matchingColor ? matchingColor.color : coursesColor, // Default color if not found
					dance_tags: matchingStyles
						? matchingStyles.map((style) => style?.name || "")
						: [], // Default to an empty array
				};
			});

			setCalendarItems(calendarItemsWithColorsAndStyleTags);
		}
	}, [eventTypeNodes, danceStyleNodes, eventNodes, courseNodes]);

	const handleDanceStyleChange = (
		value: { value: number; label: string } | null
	) => setSelectedDanceStyle(value);

	const handleEventTypeChange = (
		value: { value: number; label: string } | null
	) => setSelectedEventType(value);

	const filteredCalendarItems = calendarItems.filter((item: CalendarItem) => {
		const matchesDanceStyle =
			!selectedDanceStyle ||
			(item.categories &&
				Array.isArray(item.categories) &&
				item.categories.includes(selectedDanceStyle.value));

		const matchesEventType =
			!selectedEventType ||
			(item.event_type &&
				Array.isArray(item.event_type) &&
				item.event_type.includes(selectedEventType.value));

		return matchesDanceStyle /* && matchesTeacher */ && matchesEventType;
	});

	return (
		<div className="scb-filter">
			<div className="scb-filter-header">
				<FilterPanel
					{...allOptions}
					selectedDanceStyle={selectedDanceStyle}
					selectedEventType={selectedEventType}
					onSelectDanceStyle={handleDanceStyleChange}
					onSelectEventType={handleEventTypeChange}
					legend={legend}
				/>
				<ViewType
					view={view}
					setView={setView}
				/>
			</div>
			<div className="scb-calendar-view">
				<MonthlyView
					calendarItems={filteredCalendarItems}
					view={view}
				/>
			</div>
		</div>
	);
};

export default App;
